import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from '../store';
import { API_INSTANCE, buildURLWithState, createSiteData } from './utils';
import type { Account, ResponseEvent, TDashboardQueryData, TWixQueryData } from 'src/types/types';

export type AccountSliceState = {
  data?: Account | undefined;
  connectedAccounts?: Account;
  site?: TWixQueryData | TDashboardQueryData | null;
  error?: boolean;
};
const urlSearchParams = new URLSearchParams(
  window.location.search || `?${window.location.hash.split('?')[1] || ''}`
);

export const siteData = createSiteData(urlSearchParams);
const initialState: AccountSliceState = {
  data: undefined,
  connectedAccounts: undefined,
  site: siteData || undefined,
  error: false,
};

export const fetchAccountApi = createAsyncThunk('account/fetch', async (_, { getState }) => {
  return API_INSTANCE.get<Account>(
    buildURLWithState(getState(), `/api/v1/dashboard/accounts`)
  ).then((response) => {
    return response.data;
  });
});
export const fetchConnectedAccountsApi = createAsyncThunk(
  'connected-accounts/fetch',
  async (_, { getState }) => {
    return API_INSTANCE.get<ResponseEvent>(
      buildURLWithState(getState(), `/api/v1/connected-accounts`)
    )
      .then((response) => response.data)
      .catch((error) => error);
  }
);
export const startFreeTrialApi = createAsyncThunk('account/free_trial', async (_, { getState }) => {
  return API_INSTANCE.post<ResponseEvent>(
    buildURLWithState(getState(), `/api/v1/dashboard/accounts/free_trial`)
  )
    .then((response) => response.data)
    .catch((error) => error);
});
export const updateAccountApi = createAsyncThunk('account/update', async (data, { getState }) => {
  return API_INSTANCE.put<any>(buildURLWithState(getState(), `/api/v1/dashboard/accounts`), data)
    .then((response) => response.data)
    .catch((error) => error);
});

export const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    setSiteData: (state, action: PayloadAction) => {
      state.site = action.payload;
    },
    appendSiteData: (state, action: PayloadAction) => {
      state.site = { ...state.site, ...action.payload };
    },
    config_success: (state, action: PayloadAction) => {
      state.data = action.payload;
    },
    autoUpdateSiteData: (state) => {
      const urlSearchParams = new URLSearchParams(window.location.search);
      state.site = createSiteData(urlSearchParams);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAccountApi.fulfilled, (state, action) => {
      state.error = false;
      state.data = action.payload;
    });
    builder.addCase(fetchAccountApi.pending, (state, action) => {
      state.state.data = null;
    });
    builder.addCase(fetchAccountApi.rejected, (state) => {
      state.error = true;
    });
    builder.addCase(updateAccountApi.fulfilled, (state, action) => {
      state.data = action.payload;
    });

    builder.addCase(fetchConnectedAccountsApi.fulfilled, (state, action) => {
      console.log('action', action.payload);
      state.connectedAccounts = action.payload;
    });
    builder.addCase(startFreeTrialApi.fulfilled, (state, action) => {
      state.data = action.payload;
    });
  },
});

export const { setSiteData, appendSiteData, autoUpdateSiteData } = accountSlice.actions;

export const selectAccount = (state: RootState) => state?.account.data;
export const selectAccountFetchError = (state: RootState) => state?.account.error;
export const selectSiteId = (state: RootState) => state?.account.data.last_site_accessed?.name;
export const selectWidget = (state: RootState) => state?.account.data.last_widget_accessed?.name;
export const selectConnectedAccounts = (state: RootState) => state?.account.connectedAccounts;
export const selectSiteData = (state: RootState) => state?.account.site;

export default accountSlice;
